import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    useCartContext,
    useModalsContext,
    useStoreConfigContext,
} from '/src/providers';
import {
    Loader,
    MaxSaleQtyMessage,
    QuantityButtons,
    useConfirmation,
} from '/src/components';
import { STOCK_STATUSES } from '/src/consts';
import * as styles from './styles.scss';

const CartProducts = ({ onClose }) => {
    const {
        storeConfig: {
            product_url_suffix,
        },
        translate,
        formatPrice,
    } = useStoreConfigContext();
    const {
        cart: {
            id: cartId,
            itemsV2: { items = [] } = {},
        },
        isLoadingCart,
        removeItemFromCart,
        updateCartItems,
    } = useCartContext();
    const { showModal, MODALS_TYPES } = useModalsContext();
    const { toggleConfirmation, Confirmation } = useConfirmation();
    const [productQty, setProductQty] = useState({});
    const [errors, setErrors] = useState({});

    const getPackageSize = (additionalAttributeData, displayWeight) => {
        const { value } = additionalAttributeData?.find(({ option_id }) => option_id === displayWeight) || {};

        return value;
    };

    useEffect(() => {
        let handler = null;

        if (!!Object.keys(productQty).length) {
            handler = setTimeout(() => {
                const cartItems = [];
                Object.keys(productQty).forEach(key => {
                    cartItems.push({
                        cart_item_id: key,
                        quantity: productQty[key],
                    })
                });

                updateCartItems({
                    input: {
                        cart_id: cartId,
                        cart_items: cartItems
                    }
                }).then(() => {
                    setProductQty({});

                    if (errors) {
                        setErrors({});
                    }
                }).catch(errors => {
                    setProductQty({});

                    const errorsBySKU = {};

                    errors?.forEach(({ message }) => {
                        const [, sku] = message.match(/SKU (.*):/, '$2') || [];

                        if (sku) {
                            errorsBySKU[sku] = message ;
                        }
                    });

                    if (!!Object.keys(errorsBySKU).length) {
                        setErrors({ ...errorsBySKU });
                    } else {
                        showModal(MODALS_TYPES.ERROR, { errors });
                    }
                });
            }, 1000);
        }

        return () => { clearTimeout(handler); }
    }, [productQty]);

    return (
        <div className={styles.scroll}>
            <Loader isLoading={isLoadingCart}>
                {
                    items.sort(({ product: { stock_status } }) => stock_status === STOCK_STATUSES.OUT_OF_STOCK ? -1 : 0).map(({
                        id,
                        product: {
                            sku,
                            image: { url, label },
                            url_key,
                            stock_status,
                            display_weight,
                            producttypeinenglish,
                            additional_attribute_data,
                            price_range: {
                                minimum_price: {
                                    regular_price,
                                    final_price,
                                    discount: {
                                        percent_off,
                                        amount_off,
                                    } = {},
                                },
                            },
                            stock_data: {
                                max_sale_qty
                            }
                        },
                        prices: { row_total },
                        quantity,
                    }) => (
                        <div
                            key={id}
                            className={styles.wrapper}
                        >
                            <div className={`${styles.product}${stock_status === STOCK_STATUSES.OUT_OF_STOCK ? ` ${styles.out}` : ''}`}>
                                <div className={styles.group}>
                                    <div className={styles.image}>
                                        <Link
                                            to={`/${url_key}${product_url_suffix}`}
                                            onClick={() => onClose()}
                                        >
                                            <img
                                                className={styles.img}
                                                src={`${url}?d=100x100`}
                                                alt={label}
                                            />
                                        </Link>
                                    </div>
                                    <div>
                                        <Link
                                            className={styles.name}
                                            to={`/${url_key}${product_url_suffix}`}
                                            onClick={() => onClose()}
                                        >
                                            {producttypeinenglish}
                                        </Link>
                                        <div className={styles.status}>
                                            {`${translate('Package size')} - `}
                                            <span>{getPackageSize(additional_attribute_data, display_weight)}</span>
                                        </div>
                                        <div className={`${styles.status}${stock_status === STOCK_STATUSES.OUT_OF_STOCK ? ` ${styles.error}` : ''}`}>
                                            {stock_status === STOCK_STATUSES.IN_STOCK ? translate('In stock') : translate('Out of stock')}
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.wrap}>
                                    <div className={styles.prices}>
                                        <QuantityButtons
                                            handleDelete={() => toggleConfirmation({ [id]: true })}
                                            handleRemove={() => setProductQty({ ...productQty, [id]: productQty[id] === undefined ? quantity - 1 : productQty[id] - 1 })}
                                            handleAdd={() => setProductQty({ ...productQty, [id]: productQty[id] === undefined ? quantity + 1 : productQty[id] + 1 })}
                                            quantity={ productQty[id] === undefined ? quantity : productQty[id] }
                                            isLoading={isLoadingCart || stock_status === STOCK_STATUSES.OUT_OF_STOCK}
                                            maxSaleQuantity={max_sale_qty}
                                        />
                                        {
                                            row_total?.value === 0 &&
                                            <span className={styles.price}>
                                                {formatPrice(row_total)}
                                            </span>
                                        }
                                        {
                                            row_total?.value !== 0 &&
                                            <>
                                                {
                                                    !!final_price && !!percent_off &&
                                                    <div className={styles.discounts}>
                                                        <span className={styles.throw}>
                                                            {formatPrice(regular_price)}
                                                        </span>
                                                        <span className={styles.discount}>
                                                            {`-${formatPrice({ value: amount_off, currency: regular_price.currency })}`}
                                                        </span>
                                                    </div>
                                                }
                                                {
                                                    !!regular_price &&
                                                    <span className={styles.price}>
                                                        {`${formatPrice(!!percent_off ? final_price : regular_price)}`}
                                                    </span>
                                                }
                                            </>
                                        }
                                    </div>
                                    <MaxSaleQtyMessage
                                        isMax={(productQty[id] === undefined ? quantity : productQty[id]) === max_sale_qty}
                                        message={translate('Maximum Qty Allowed in Shopping Cart %1', max_sale_qty)}
                                    />
                                </div>
                            </div>
                            <button
                                className={styles.remove}
                                onClick={() => toggleConfirmation({ [id]: true })}
                                disabled={isLoadingCart}
                            >
                                {translate('Remove')}
                            </button>
                            <Confirmation
                                id={id}
                                onSuccess={
                                    () => removeItemFromCart({
                                        input: {
                                            cart_id: cartId,
                                            cart_item_id: id
                                        }
                                    }).catch(errors => showModal(MODALS_TYPES.ERROR, { errors }))
                                }
                                disabled={isLoadingCart}
                            />
                            {
                                !!errors[sku] &&
                                <div className={styles.error}>
                                    {errors[sku]}
                                </div>
                            }
                        </div>
                    ))
                }
            </Loader>
        </div>
    )
};

export default CartProducts;
